import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RotateCcw, Calculator, Info, ShieldCheck } from "lucide-react";
import CTAButtons from "@/components/CTAButtons";

// Companies Regulation 26 + 28 — Public Interest Score formula:
// 1 point per average employee
// 1 point per R1m (or part) of third-party liabilities at year-end
// 1 point per R1m (or part) of turnover during the financial year
// 1 point per individual who at year-end is a beneficial holder of any of the company's securities
// (or, for an NPC, per member)
const fmtR = (n) =>
    new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(n);

function calcPIS({ employees, liabilities, turnover, holders }) {
    const e = Math.max(0, Number(employees) || 0);
    const l = Math.ceil(Math.max(0, Number(liabilities) || 0) / 1_000_000);
    const t = Math.ceil(Math.max(0, Number(turnover) || 0) / 1_000_000);
    const h = Math.max(0, Number(holders) || 0);
    return { e, l, t, h, total: e + l + t + h };
}

function classify(score, internallyCompiled) {
    if (score >= 350) {
        return {
            level: "Audit",
            tone: "fail",
            text: "Your AFS must be audited by a Registered Auditor (RA) — Independent Reviews and Compilations are not enough at this score.",
        };
    }
    if (score >= 100) {
        if (internallyCompiled) {
            return {
                level: "Audit",
                tone: "fail",
                text: "Because your AFS are internally compiled (not by an independent practitioner), the audit threshold drops from 350 to 100. You must therefore have an audit.",
            };
        }
        return {
            level: "Independent Review",
            tone: "warn",
            text: "An Independent Review under ISRE 2400 is acceptable. AFS prepared by an independent practitioner unlocks this lighter assurance level.",
        };
    }
    return {
        level: "Independent Review",
        tone: "ok",
        text: "Even at low scores, the Companies Act requires either an Audit or an Independent Review, unless the company is owner-managed (every shareholder is also a director and there are no third-party shareholders).",
    };
}

export default function PISCalculator() {
    const [form, setForm] = useState({ employees: "", liabilities: "", turnover: "", holders: "", internallyCompiled: false, ownerManaged: false });

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
    const reset = () => {
        setForm({ employees: "", liabilities: "", turnover: "", holders: "", internallyCompiled: false, ownerManaged: false });
        requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    };

    const result = calcPIS(form);
    const { total } = result;

    const ruling = classify(total, form.internallyCompiled);
    const ownerExempt =
        form.ownerManaged && total < 100
            ? {
                  level: "Compilation only",
                  tone: "ok",
                  text: "An owner-managed company (every shareholder is also a director, no third-party shareholders) with a PIS below 100 may have its AFS compiled — no Audit or Independent Review required by the Companies Act. The MOI may still mandate an audit.",
              }
            : null;

    const finalRuling = ownerExempt || ruling;

    const tones = {
        ok: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700" },
        warn: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700" },
        fail: { bg: "bg-red-50", border: "border-red-300", text: "text-red-700" },
    };
    const t = tones[finalRuling.tone];

    return (
        <div className="bg-tca-fog min-h-screen" data-testid="page-pis-calculator">
            <div className="bg-white border-b border-tca-line">
                <div className="container-tca py-4 flex items-center justify-between gap-3">
                    <Link to="/checklists" className="btn-ghost text-base gap-2.5" data-testid="back-to-checklists">
                        <ArrowLeft className="w-5 h-5" /> All checklists
                    </Link>
                    <button onClick={reset} className="btn-ghost" data-testid="pis-reset">
                        <RotateCcw className="w-4 h-4" /> Reset
                    </button>
                </div>
            </div>

            <section className="container-tca py-12 md:py-16">
                <div className="max-w-4xl mx-auto">
                    <div className="mb-8">
                        <p className="overline mb-3">Companies Regulation 26 + 28</p>
                        <h1 className="font-display font-extrabold text-tca-ink text-4xl sm:text-5xl leading-tight">Audit vs Independent Review (PIS) Calculator</h1>
                        <p className="mt-4 text-tca-mute leading-relaxed max-w-2xl">
                            The Public Interest Score determines whether your company's AFS must be audited or independently reviewed. Enter four numbers — we compute the score and the ruling instantly.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Inputs */}
                        <div className="lg:col-span-7 bg-white border border-tca-line p-7 sm:p-9 space-y-5" data-testid="pis-form">
                            {[
                                { k: "employees", label: "Average number of employees during the year", helper: "Use the average — not year-end head count.", suffix: "employees" },
                                { k: "liabilities", label: "Third-party liabilities at year-end (ZAR)", helper: "Excludes shareholder / director loan accounts.", suffix: "ZAR" },
                                { k: "turnover", label: "Turnover for the financial year (ZAR)", helper: "Gross income from sales / services.", suffix: "ZAR" },
                                { k: "holders", label: "Individual beneficial holders of securities at year-end", helper: "For NPCs, the number of members.", suffix: "holders" },
                            ].map((f) => (
                                <div key={f.k}>
                                    <label className="overline mb-2 block">{f.label}</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            min="0"
                                            value={form[f.k]}
                                            onChange={update(f.k)}
                                            data-testid={`pis-input-${f.k}`}
                                            className="w-full border border-tca-line bg-white px-4 py-3 pr-20 text-base focus:outline-none focus:border-tca-orange focus:ring-2 focus:ring-tca-orangeSoft"
                                            placeholder="0"
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-tca-mute">{f.suffix}</span>
                                    </div>
                                    <p className="mt-1.5 text-xs text-tca-mute flex items-start gap-1.5">
                                        <Info className="w-3.5 h-3.5 mt-0.5 text-tca-orange shrink-0" /> {f.helper}
                                    </p>
                                </div>
                            ))}

                            <div className="pt-4 border-t border-tca-line space-y-3">
                                <label className="flex items-start gap-3 cursor-pointer" data-testid="pis-toggle-internally-compiled">
                                    <input
                                        type="checkbox"
                                        checked={form.internallyCompiled}
                                        onChange={update("internallyCompiled")}
                                        className="w-4 h-4 mt-1 accent-tca-orange"
                                    />
                                    <span className="text-sm text-tca-ink leading-snug">
                                        Our AFS are <strong>internally compiled</strong> (not by an independent practitioner)
                                        <span className="block text-xs text-tca-mute mt-0.5">If yes, the audit threshold drops from 350 to 100.</span>
                                    </span>
                                </label>
                                <label className="flex items-start gap-3 cursor-pointer" data-testid="pis-toggle-owner-managed">
                                    <input
                                        type="checkbox"
                                        checked={form.ownerManaged}
                                        onChange={update("ownerManaged")}
                                        className="w-4 h-4 mt-1 accent-tca-orange"
                                    />
                                    <span className="text-sm text-tca-ink leading-snug">
                                        We are <strong>owner-managed</strong> (every shareholder is also a director, no outside shareholders)
                                        <span className="block text-xs text-tca-mute mt-0.5">May exempt the company from Independent Review at low PIS.</span>
                                    </span>
                                </label>
                            </div>
                        </div>

                        {/* Result */}
                        <div className="lg:col-span-5 space-y-6">
                            <div className="bg-tca-ink text-white p-7 sm:p-9" data-testid="pis-score-card">
                                <div className="flex items-center gap-2 mb-4">
                                    <Calculator className="w-5 h-5 text-tca-orange" />
                                    <p className="overline text-white/60">Public Interest Score</p>
                                </div>
                                <p className="font-display font-extrabold text-7xl sm:text-8xl text-white tabular-nums leading-none" data-testid="pis-score-value">
                                    {total}
                                </p>
                                <p className="text-xs text-white/60 mt-3">points</p>
                                <div className="grid grid-cols-2 gap-3 mt-6 text-xs">
                                    <div>
                                        <p className="text-white/60">Employees</p>
                                        <p className="font-display font-bold text-white">+{result.e}</p>
                                    </div>
                                    <div>
                                        <p className="text-white/60">Liabilities</p>
                                        <p className="font-display font-bold text-white">+{result.l}</p>
                                    </div>
                                    <div>
                                        <p className="text-white/60">Turnover</p>
                                        <p className="font-display font-bold text-white">+{result.t}</p>
                                    </div>
                                    <div>
                                        <p className="text-white/60">Holders</p>
                                        <p className="font-display font-bold text-white">+{result.h}</p>
                                    </div>
                                </div>
                            </div>

                            <div className={`${t.bg} border ${t.border} p-6`} data-testid="pis-ruling">
                                <div className="flex items-center gap-2 mb-2">
                                    <ShieldCheck className={`w-5 h-5 ${t.text}`} />
                                    <p className={`overline ${t.text}`}>Required assurance</p>
                                </div>
                                <h3 className="font-display font-extrabold text-tca-ink text-3xl mb-2" data-testid="pis-ruling-level">{finalRuling.level}</h3>
                                <p className="text-sm text-tca-ink leading-relaxed">{finalRuling.text}</p>
                            </div>

                            <div className="border border-tca-line bg-white p-6">
                                <p className="overline mb-2">Common thresholds</p>
                                <ul className="space-y-1.5 text-sm">
                                    <li className="flex justify-between"><span className="text-tca-mute">PIS &lt; 100</span><span className="font-semibold">Compilation may suffice (owner-managed)</span></li>
                                    <li className="flex justify-between"><span className="text-tca-mute">PIS 100 – 349</span><span className="font-semibold">Independent Review</span></li>
                                    <li className="flex justify-between"><span className="text-tca-mute">PIS ≥ 350</span><span className="font-semibold">Audit (RA-signed)</span></li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="bg-tca-orangeSoft/40 border border-tca-orange p-6 mt-10" data-testid="pis-cta">
                        <p className="font-display font-bold text-tca-ink mb-1">Want this confirmed by an RA?</p>
                        <p className="text-sm text-tca-ink leading-relaxed mb-4 max-w-2xl">
                            Your MOI may also override the default by requiring an audit. We compute and document the PIS for every TC Auditors client annually and lodge it with the AFS file.
                        </p>
                        <CTAButtons testIdPrefix="pis-cta" />
                    </div>

                    <p className="text-xs text-tca-mute mt-6 italic leading-relaxed max-w-3xl">
                        This calculator implements Companies Regulation 26 (formula) and Regulation 28 (audit / review thresholds). Certain regulated entities (e.g. financial institutions, JSE-listed) must be audited regardless of PIS.
                    </p>
                </div>
            </section>
        </div>
    );
}
